import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaChartLine, FaShieldAlt, FaGlobe, FaUserTie, FaCertificate, FaBuilding, FaArrowRight } from 'react-icons/fa';
import { api } from '../App';
import { useLanguage } from '../LanguageContext';
import SEO from '../components/SEO';
import './Home.css';

const Home = () => {
  const [latestPost, setLatestPost] = useState(null);
  const { t, language } = useLanguage();

  useEffect(() => {
    const fetchLatestPost = async () => {
      try {
        const data = await api.get('/api/posts?limit=1&category=weekly-report');
        if (data.posts?.length > 0) {
          setLatestPost(data.posts[0]);
        }
      } catch (error) {
        console.error('Failed to fetch latest post:', error);
      }
    };
    fetchLatestPost();
  }, []);

  const features = [
    {
      icon: <FaBuilding />,
      number: '01',
      title: t('home.feature1Title'),
      description: t('home.feature1Desc')
    },
    {
      icon: <FaGlobe />,
      number: '02',
      title: t('home.feature2Title'),
      description: t('home.feature2Desc')
    },
    {
      icon: <FaChartLine />,
      number: '03',
      title: t('home.feature3Title'),
      description: t('home.feature3Desc')
    },
    {
      icon: <FaCertificate />,
      number: '04',
      title: t('home.feature4Title'),
      description: t('home.feature4Desc')
    },
    {
      icon: <FaShieldAlt />,
      number: '05',
      title: t('home.feature5Title'),
      description: t('home.feature5Desc')
    },
    {
      icon: <FaUserTie />,
      number: '06',
      title: t('home.feature6Title'),
      description: t('home.feature6Desc')
    }
  ];

  return (
    <div className="home">
      <SEO 
        title={t('home.heroTitle') + ' ' + t('home.heroTitleGold')}
        description={t('home.heroDescription')}
        keywords="crypto funds, bitcoin investment, ethereum fund, EU regulated crypto, alternative investment funds, Slovenia, professional investors"
      />
      
      {/* Hero Section */}
      <section className="hero">
        <div className="hero-bg">
          <div className="hero-gradient"></div>
          <div className="hero-pattern"></div>
        </div>
        
        {/* Golden Bull Image */}
        <motion.img 
          src="/bik.png" 
          alt="Golden Bull" 
          className="golden-bull-img"
          initial={{ opacity: 0, x: 100 }}
          animate={{ opacity: 0.8, x: 0 }}
          transition={{ duration: 1, delay: 0.3 }}
        />
        
        <div className="container hero-content">
          <motion.div
            className="hero-text"
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="hero-title">
              {t('home.heroTitle')}<br />
              <span className="gold-text">{t('home.heroTitleGold')}</span>
            </h1>
            <p className="hero-description">
              {t('home.heroDescription')}
            </p>
            <div className="hero-features">
              <div className="hero-feature">
                <span className="feature-dot"></span>
                {t('home.featureProfessional')}
              </div>
              <div className="hero-feature">
                <span className="feature-dot"></span>
                {t('home.featurePromising')}
              </div>
              <div className="hero-feature">
                <span className="feature-dot"></span>
                {t('home.featureLowCosts')}
              </div>
              <div className="hero-feature">
                <span className="feature-dot"></span>
                {t('home.featureSecurity')}
              </div>
            </div>
            <div className="hero-actions">
              <a 
                href="https://calendar.app.google/HApyWJxdUvwEQNLL6"
                target="_blank" 
                rel="noopener noreferrer"
                className="btn btn-primary"
              >
                {t('nav.bookConsultation')}
              </a>
              <Link to="/about" className="btn btn-outline">
                {t('home.learnMore')}
              </Link>
            </div>
          </motion.div>
        </div>

        <motion.div 
          className="hero-scroll"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1, duration: 0.5 }}
        >
          <span>{t('home.scrollToExplore')}</span>
          <div className="scroll-line"></div>
        </motion.div>
      </section>

      {/* Disclaimer Banner */}
      <div className="disclaimer-banner">
        <div className="container">
          <p>{t('home.disclaimer')}</p>
        </div>
      </div>

      {/* Features Section */}
      <section className="features-section section">
        <div className="container">
          <div className="section-header">
            <span className="section-subtitle">{t('home.whyChooseUs')}</span>
            <h2 className="section-title">FUTURE MONEY</h2>
            <div className="gold-line"></div>
          </div>

          <div className="features-grid">
            {features.map((feature, index) => (
              <motion.div
                key={feature.number}
                className="feature-card"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1, duration: 0.5 }}
              >
                <div className="feature-number">{feature.number}</div>
                <div className="feature-icon">{feature.icon}</div>
                <h3 className="feature-title">{feature.title}</h3>
                <p className="feature-description">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Funds Section */}
      <section className="funds-section section">
        <div className="container">
          <div className="section-header">
            <span className="section-subtitle">{t('home.ourFunds')}</span>
            <h2 className="section-title">{t('home.threePaths')}</h2>
            <div className="gold-line"></div>
          </div>

          <div className="funds-grid">
            <motion.div
              className="fund-card"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <div className="fund-badge">Fund 1</div>
              <h3 className="fund-title">{t('home.fund1Title')}</h3>
              <p className="fund-description">
                {t('home.fund1Desc')}
              </p>
              <ul className="fund-features">
                <li>{t('home.fund1Feature1')}</li>
                <li>{t('home.fund1Feature2')}</li>
                <li>{t('home.fund1Feature3')}</li>
              </ul>
            </motion.div>

            <motion.div
              className="fund-card featured"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1, duration: 0.5 }}
            >
              <div className="fund-badge">Fund 2</div>
              <h3 className="fund-title">{t('home.fund2Title')}</h3>
              <p className="fund-description">
                {t('home.fund2Desc')}
              </p>
              <ul className="fund-features">
                <li>{t('home.fund2Feature1')}</li>
                <li>{t('home.fund2Feature2')}</li>
                <li>{t('home.fund2Feature3')}</li>
              </ul>
            </motion.div>

            <motion.div
              className="fund-card"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2, duration: 0.5 }}
            >
              <div className="fund-badge">Fund 3</div>
              <h3 className="fund-title">{t('home.fund3Title')}</h3>
              <p className="fund-description">
                {t('home.fund3Desc')}
              </p>
              <ul className="fund-features">
                <li>{t('home.fund3Feature1')}</li>
                <li>{t('home.fund3Feature2')}</li>
                <li>{t('home.fund3Feature3')}</li>
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Weekly Report Section */}
      {latestPost && (
        <section className="report-section section">
          <div className="container">
            <div className="report-container">
              <div className="report-header">
                <span className="section-subtitle">{t('home.weeklyReport')}</span>
                <h2 className="section-title">{t('home.marketAnalysis')}</h2>
              </div>
              
              <div className="report-content">
                <div className="report-date">
                  {new Date(latestPost.published_at).toLocaleDateString(language === 'sl' ? 'sl-SI' : 'en-US', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric'
                  })}
                </div>
                <h3 className="report-title">{latestPost.title}</h3>
                <p className="report-excerpt">{latestPost.excerpt}</p>
                <Link to={`/blog/${latestPost.slug}`} className="btn btn-outline">
                  {t('home.readFullReport')} <FaArrowRight />
                </Link>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="cta-section section">
        <div className="container">
          <motion.div
            className="cta-content"
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="cta-title">{t('home.readyToInvest')}</h2>
            <p className="cta-description">
              {t('home.ctaDescription')}
            </p>
            <a 
              href="https://calendar.app.google/HApyWJxdUvwEQNLL6"
              target="_blank" 
              rel="noopener noreferrer"
              className="btn btn-primary btn-large"
            >
              {t('home.bookFreeConsultation')}
            </a>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Home;
